import { Redis } from '@upstash/redis';

// Cache configuration based on TMDB requirements and content types
export const CACHE_DURATIONS = {
  // Static content - can cache longer
  MOVIE_DETAILS: 30 * 24 * 60 * 60, // 30 days for completed movies
  PERSON_DETAILS: 30 * 24 * 60 * 60, // 30 days for person info
  GENRES: 90 * 24 * 60 * 60, // 90 days for genre lists

  // Dynamic content - shorter cache
  TV_SHOW_DETAILS: 6 * 60 * 60, // 6 hours for TV shows (may have new episodes)
  TV_SEASON: 12 * 60 * 60, // 12 hours for season details
  TV_EPISODE: 6 * 60 * 60, // 6 hours for episode info

  // Frequently changing content
  TRENDING: 1 * 60 * 60, // 1 hour for trending
  POPULAR: 3 * 60 * 60, // 3 hours for popular lists
  TOP_RATED: 6 * 60 * 60, // 6 hours for top rated
  NOW_PLAYING: 1 * 60 * 60, // 1 hour for now playing
  ON_THE_AIR: 1 * 60 * 60, // 1 hour for currently airing TV

  // Search results
  SEARCH: 24 * 60 * 60, // 24 hours for search results

  // Recommendations
  RECOMMENDATIONS: 12 * 60 * 60, // 12 hours for recommendations
  SIMILAR: 24 * 60 * 60, // 24 hours for similar content
};

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  contentType: string;
  hasNewContent?: boolean; // For TV shows
}

class TMDBCache {
  private memoryCache: Map<string, CacheEntry>;
  private maxMemorySize = 100; // Maximum items in memory cache

  constructor() {
    this.memoryCache = new Map();
  }

  // Generate cache key
  private getCacheKey(type: string, id?: string | number, params?: any): string {
    const baseKey = `tmdb:${type}`;
    if (id) {
      const paramString = params ? `:${JSON.stringify(params)}` : '';
      return `${baseKey}:${id}${paramString}`;
    }
    return baseKey;
  }

  // Get cache duration based on content type
  private getCacheDuration(contentType: string, data?: any): number {
    // Special handling for TV shows
    if (contentType === 'TV_SHOW_DETAILS' && data) {
      // If show is ended, cache longer
      if (data.status === 'Ended' || data.status === 'Canceled') {
        return 30 * 24 * 60 * 60; // 30 days for ended shows
      }
      // If show has next episode soon, cache shorter
      if (data.next_episode_to_air) {
        const airDate = new Date(data.next_episode_to_air.air_date);
        const now = new Date();
        const hoursUntilAir = (airDate.getTime() - now.getTime()) / (1000 * 60 * 60);

        if (hoursUntilAir <= 24) {
          return 1 * 60 * 60; // 1 hour if airing within 24 hours
        }
      }
    }

    // For movies, check if they're released
    if (contentType === 'MOVIE_DETAILS' && data) {
      if (data.status === 'Released') {
        return CACHE_DURATIONS.MOVIE_DETAILS;
      }
      // Upcoming movies get shorter cache
      return 24 * 60 * 60; // 24 hours for unreleased movies
    }

    return CACHE_DURATIONS[contentType as keyof typeof CACHE_DURATIONS] || 3600;
  }

  // Set cache entry
  async set<T>(
    type: string,
    id: string | number | undefined,
    data: T,
    contentType: string,
    params?: any
  ): Promise<void> {
    const key = this.getCacheKey(type, id, params);
    const ttl = this.getCacheDuration(contentType, data);

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      contentType,
    };

    // Check for new content in TV shows
    if (contentType === 'TV_SHOW_DETAILS' && typeof data === 'object' && data !== null) {
      const show = data as any;
      if (show.next_episode_to_air || show.last_episode_to_air) {
        entry.hasNewContent = true;
      }
    }

    // Store in memory cache
    this.memoryCache.set(key, entry);

    // Manage memory cache size
    if (this.memoryCache.size > this.maxMemorySize) {
      const firstKey = this.memoryCache.keys().next().value;
      this.memoryCache.delete(firstKey);
    }

    // Store in Redis if configured (works in both dev and production)
    if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
      await this.setRedisCache(key, entry);
    }
  }

  // Get cache entry
  async get<T>(
    type: string,
    id?: string | number,
    params?: any
  ): Promise<T | null> {
    const key = this.getCacheKey(type, id, params);

    // Check memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry) {
      const age = Date.now() - memoryEntry.timestamp;
      if (age < memoryEntry.ttl * 1000) {
        return memoryEntry.data as T;
      } else {
        this.memoryCache.delete(key);
      }
    }

    // Check Redis if configured (works in both dev and production)
    if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
      return await this.getRedisCache<T>(key);
    }

    return null;
  }

  // Invalidate cache for specific content
  async invalidate(type: string, id?: string | number): Promise<void> {
    const pattern = this.getCacheKey(type, id);

    // Clear from memory cache
    if (id) {
      // Clear specific entry
      for (const key of this.memoryCache.keys()) {
        if (key.startsWith(pattern)) {
          this.memoryCache.delete(key);
        }
      }
    } else {
      // Clear all entries of this type
      for (const key of this.memoryCache.keys()) {
        if (key.startsWith(`tmdb:${type}`)) {
          this.memoryCache.delete(key);
        }
      }
    }

    // Clear from Redis if configured
    if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
      await this.invalidateRedisCache(pattern);
    }
  }

  // Force refresh for TV shows with new content
  async checkAndRefreshTVShow(showId: number): Promise<boolean> {
    const key = this.getCacheKey('tv', showId);
    const entry = this.memoryCache.get(key);

    if (entry && entry.contentType === 'TV_SHOW_DETAILS') {
      const show = entry.data as any;

      // Check if we should refresh based on show status
      if (show.status === 'Returning Series' || show.in_production) {
        const age = Date.now() - entry.timestamp;
        const maxAge = 6 * 60 * 60 * 1000; // 6 hours for active shows

        if (age > maxAge) {
          await this.invalidate('tv', showId);
          return true; // Needs refresh
        }
      }
    }

    return false;
  }

  // Redis cache methods (for production)
  private async setRedisCache(key: string, entry: CacheEntry): Promise<void> {
    try {
      if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
        const redis = new Redis({
          url: process.env.UPSTASH_REDIS_REST_URL,
          token: process.env.UPSTASH_REDIS_REST_TOKEN,
        });

        // Upstash automatically handles JSON serialization
        await redis.set(key, entry, { ex: entry.ttl });
      }
    } catch (error) {
      // Redis cache set error
    }
  }

  private async getRedisCache<T>(key: string): Promise<T | null> {
    try {
      if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
        const redis = new Redis({
          url: process.env.UPSTASH_REDIS_REST_URL,
          token: process.env.UPSTASH_REDIS_REST_TOKEN,
        });

        // Upstash automatically deserializes JSON
        const entry = await redis.get<CacheEntry<T>>(key);
        if (entry) {
          const age = Date.now() - entry.timestamp;
          if (age < entry.ttl * 1000) {
            return entry.data;
          }
        }
      }
    } catch (error) {
      // Redis cache get error
    }
    return null;
  }

  private async invalidateRedisCache(pattern: string): Promise<void> {
    try {
      if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
        const redis = new Redis({
          url: process.env.UPSTASH_REDIS_REST_URL,
          token: process.env.UPSTASH_REDIS_REST_TOKEN,
        });

        // Delete keys matching pattern
        const keys = await redis.keys(pattern + '*');
        if (keys.length > 0) {
          await redis.del(...keys);
        }
      }
    } catch (error) {
      // Redis cache invalidate error
    }
  }

  // Get cache statistics
  getCacheStats() {
    return {
      memorySize: this.memoryCache.size,
      entries: Array.from(this.memoryCache.entries()).map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        ttl: entry.ttl,
        contentType: entry.contentType,
      })),
    };
  }
}

// Export singleton instance
export const tmdbCache = new TMDBCache();